import { DocumentNode, useMutation } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import React from 'react';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import { IDocument } from '../../../utils/statApi/DocumentApi';
import useDocumentsList from '../../../utils/hooks/useDocumentsList';
import ActionButtons from '../ActionButtons';
import LoadingErrorMessage from '../errorContent/LoadingErrorMessage';
import DocumentsForm from './DocumentsForm';

interface IDocumentsPanelProps {
  closePanel: () => void;
  initialDocuments: IDocument[];
  parentId: number;
  documentType: string;
  maxOfOne?: boolean;
  mutation: DocumentNode;
  showDeletedFiles: boolean;
  panelTitle?: string;
  documentTypeIsServerDefined?: boolean;
  /** When false, the panel only allows viewing/removing existing documents. */
  allowUpload?: boolean;
  uploadDisabledMessage?: string;
}

const DocumentsPanel = ({
  closePanel,
  initialDocuments,
  parentId,
  documentType,
  maxOfOne = false,
  mutation,
  showDeletedFiles,
  panelTitle,
  documentTypeIsServerDefined = false,
  allowUpload = true,
  uploadDisabledMessage,
}: IDocumentsPanelProps): JSX.Element => {
  const {
    currentDocuments,
    newDocuments,
    documentIdsToRemove,
    uploadIsInProgress,
    attachDocuments,
    deleteDocument,
    setUploadInProgress,
  } = useDocumentsList(initialDocuments, maxOfOne);

  const [updateCompanyCreationDocuments, { loading, error }] = useMutation(mutation, {
    onCompleted: closePanel,
  });

  const panelTitleText = panelTitle || documentType;

  const saveDocumentLists = (): void => {
    const input = {
      parentId,
      documentsToAdd: newDocuments,
      documentIdsToRemove,
      showDeletedFiles,
      ...(!documentTypeIsServerDefined && { documentType: documentType.toUpperCase() }),
    };

    updateCompanyCreationDocuments({
      variables: {
        input,
      },
    });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText={`Edit ${panelTitleText} Documents`}
      isOpen
      onDismiss={closePanel}
      hasCloseButton
      styles={CoherencePanelStyles}
      closeButtonAriaLabel={`Close Edit ${panelTitleText} Documents Panel`}
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          mutationLoading={loading || uploadIsInProgress}
          closePanel={closePanel}
          handleSubmit={saveDocumentLists}
          saveLabel={`Save ${panelTitleText} Documents`}
          saveTitle={`Save ${panelTitleText} Documents`}
          cancelLabel={`Cancel ${panelTitleText} Documents Edit`}
          cancelTitle={`Cancel ${panelTitleText} Documents Edit`}
        />
      )}
    >
      <form onSubmit={saveDocumentLists}>
        <LoadingErrorMessage error={error} loading={loading} />
        <DocumentsForm
          documents={currentDocuments}
          attachDocument={attachDocuments}
          deleteDocument={deleteDocument}
          maxOfOne={maxOfOne}
          uploadInProgress={setUploadInProgress}
          allowUpload={allowUpload}
          uploadDisabledMessage={uploadDisabledMessage}
        />
      </form>
    </CoherencePanel>
  );
};

export default DocumentsPanel;
